// Public buyer checkout does not show delivery fees yet. Flip this to true when that checkout ships.
export const SHOW_REGIONAL_DELIVERY_FEES = false;

export const MATCH_STATE = 'state';
export const MATCH_POSTAL_PREFIX = 'postal_prefix';
export const MATCH_CITY = 'city';

export const MATCH_TYPES = [
  { value: MATCH_STATE, text: 'State / region' },
  { value: MATCH_POSTAL_PREFIX, text: 'Postal prefix' },
  { value: MATCH_CITY, text: 'City' },
];

export const EAST_MALAYSIA_CODES = ['MY-12', 'MY-13', 'MY-15'];
export const EAST_MALAYSIA_NAMES = ['SABAH', 'SARAWAK', 'LABUAN'];

export function forceRegionalDeliveryFeesOff(settings) {
  if (SHOW_REGIONAL_DELIVERY_FEES || !settings || typeof settings !== 'object') {
    return settings;
  }

  const fees = settings.regional_delivery_fees;
  if (!fees || typeof fees !== 'object') {
    return settings;
  }

  fees.enabled = false;
  return settings;
}

export function defaultAdvancedSettings() {
  return {
    allowed_countries: {
      enabled: false,
      countries: [],
    },
    regional_delivery_fees: {
      enabled: false,
      default_fee_cents: 0,
      regions: [],
    },
  };
}

export function toBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function dollarsToCents(value) {
  return Math.max(0, Math.round((Number(value) || 0) * 100));
}

export function centsToDollars(value) {
  return (Number(value) || 0) / 100;
}

export function slugify(value) {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `region-${Date.now()}`;
}

export function normalizeCountryCode(value) {
  return String(value || '').trim().toUpperCase();
}

export function normalizeMatchValues(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return Array.from(new Set(values
    .map((value) => String(value || '').trim())
    .filter(Boolean)));
}

export function createEmptyRegion(overrides = {}) {
  return {
    id: overrides.id || `region-${Date.now()}`,
    name: overrides.name || '',
    country: normalizeCountryCode(overrides.country || ''),
    match_type: overrides.match_type || MATCH_STATE,
    match_values: normalizeMatchValues(overrides.match_values || []),
    fee_cents: Math.max(0, parseInt(overrides.fee_cents, 10) || 0),
  };
}

export function normalizeRegion(region, index = 0) {
  if (!region || typeof region !== 'object') {
    return createEmptyRegion({ id: `region-${index + 1}` });
  }

  return createEmptyRegion({
    id: region.id || slugify(region.name) || `region-${index + 1}`,
    name: region.name || '',
    country: region.country,
    match_type: [MATCH_STATE, MATCH_POSTAL_PREFIX, MATCH_CITY].includes(region.match_type)
      ? region.match_type
      : MATCH_STATE,
    match_values: region.match_values,
    fee_cents: region.fee_cents,
  });
}

export function normalizeAdvancedSettings(incoming) {
  const defaults = defaultAdvancedSettings();
  const source = incoming && typeof incoming === 'object' ? incoming : {};
  const allowed = source.allowed_countries && typeof source.allowed_countries === 'object'
    ? source.allowed_countries
    : {};
  const fees = source.regional_delivery_fees && typeof source.regional_delivery_fees === 'object'
    ? source.regional_delivery_fees
    : {};

  defaults.allowed_countries.enabled = toBool(allowed.enabled);
  defaults.allowed_countries.countries = Array.from(new Set(
    (Array.isArray(allowed.countries) ? allowed.countries : [])
      .map(normalizeCountryCode)
      .filter(Boolean)
  ));
  defaults.regional_delivery_fees.enabled = toBool(fees.enabled);
  defaults.regional_delivery_fees.default_fee_cents = Math.max(
    0,
    parseInt(fees.default_fee_cents, 10) || 0
  );
  defaults.regional_delivery_fees.regions = (Array.isArray(fees.regions) ? fees.regions : [])
    .map(normalizeRegion);

  return defaults;
}

export function regionIdentityTokens(region) {
  const tokens = [];
  const name = String(region && region.name ? region.name : '').trim();
  const code = String(region && region.code ? region.code : '').trim().toUpperCase();

  if (name) {
    tokens.push(name);
  }
  if (code) {
    tokens.push(code);
    if (code.includes('-')) {
      tokens.push(code.split('-').pop());
    }
  }

  return Array.from(new Set(tokens.filter(Boolean)));
}

export function isEastMalaysiaRegion(region) {
  const code = String(region && region.code ? region.code : '').trim().toUpperCase();
  const name = String(region && region.name ? region.name : '').trim().toUpperCase();

  if (EAST_MALAYSIA_CODES.includes(code)) {
    return true;
  }

  return EAST_MALAYSIA_NAMES.some((token) => name.includes(token));
}

export function splitMalaysiaRegions(catalogRegions) {
  const regions = Array.isArray(catalogRegions) ? catalogRegions : [];
  const east = [];
  const west = [];

  regions.forEach((region) => {
    if (isEastMalaysiaRegion(region)) {
      east.push(region);
    } else {
      west.push(region);
    }
  });

  return { west, east };
}

export function malaysiaPreset(kind, catalogRegions, feeCents = 0) {
  const { west, east } = splitMalaysiaRegions(catalogRegions);
  const isEast = kind === 'east';
  const source = isEast ? east : west;

  return createEmptyRegion({
    id: isEast ? 'east-my' : 'west-my',
    name: isEast ? 'East Malaysia' : 'West Malaysia',
    country: 'MY',
    match_type: MATCH_STATE,
    match_values: source.map((region) => String(region && region.name ? region.name : '').trim()).filter(Boolean),
    fee_cents: feeCents,
  });
}
